import React, { useEffect, useState, useMemo, useCallback } from 'react'
import {
  listFamilyReportCards,
  getFamilyReportCard,
  type EduReportCardDoc,
  type EduReportCardSummary,
} from '../../services/eduReportCardService'
import {
  getEduPortfolio,
  getEduPortfolioPdfUrl,
  setEduPortfolioFamilyHide,
  type EduPortfolioItem,
} from '../../services/eduPortfolioService'
import { getEduCertificatePdfUrl } from '../../services/eduProgressService'
import {
  listFamilyEduPtmSummaries,
  type EduPtmSummaryItem,
} from '../../services/eduPtmService'
import {
  getFamilyProgress,
  type FamilyLearnerRow,
  type GetFamilyProgressResult,
} from '../../services/eduFamilyService'
import { ReportCardDocument } from '../Education/ReportCardDocument'
import { Button, FamilyCard, MiniLoader, Select, StatusMark, Toggle } from '../Common'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import { useNls } from '../../nls/nlsProvider'
import { formatEduDate } from '../../utils/eduFamilyFormat'
import { FamilyTrainingSection } from './FamilyTrainingSection'
import './familyProgressTab.css'

export interface FamilyProgressTabProps {
  workspaceId: string
  learnerId: string
  trainingProgrammes?: Array<{ rootId: string; title: string }>
  frozen?: boolean
  learner?: FamilyLearnerRow
}

export const FamilyProgressTab: React.FC<FamilyProgressTabProps> = ({
  workspaceId,
  learnerId,
  trainingProgrammes = [],
  frozen = false,
}) => {
  const { t, locale } = useNls()
  const [reloadSeq, setReloadSeq] = useState<number>(0)
  const [reloadCardSeq, setReloadCardSeq] = useState<number>(0)
  const [cards, setCards] = useState<EduReportCardSummary[]>([])
  const [activeCardId, setActiveCardId] = useState<string>('')
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [versionCard, setVersionCard] = useState<EduReportCardDoc | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [cardLoading, setCardLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [cardError, setCardError] = useState<string | null>(null)

  // PTM Summaries state
  const [ptmSummaries, setPtmSummaries] = useState<EduPtmSummaryItem[]>([])
  const [loadingPtmSummaries, setLoadingPtmSummaries] = useState<boolean>(false)
  const [ptmSummariesError, setPtmSummariesError] = useState<string | null>(null)

  // Attendance and marks state (EM8.b)
  const [progressData, setProgressData] = useState<GetFamilyProgressResult | null>(null)
  const [loadingProgress, setLoadingProgress] = useState<boolean>(true)
  const [progressError, setProgressError] = useState<string | null>(null)
  const [reloadProgressSeq, setReloadProgressSeq] = useState<number>(0)
  const [progressLearnerId, setProgressLearnerId] = useState<string>(learnerId)

  if (progressLearnerId !== learnerId) {
    setProgressLearnerId(learnerId)
    setProgressData(null)
    setProgressError(null)
    setLoadingProgress(true)
  }

  useEffect(() => {
    let cancelled = false

    getFamilyProgress({ workspaceId, learnerId })
      .then((res) => {
        if (!cancelled) {
          setProgressData(res)
          setLoadingProgress(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setProgressError(mapEduServerError(err, t))
          setLoadingProgress(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [workspaceId, learnerId, reloadProgressSeq, t])

  // Portfolio state (EQ200)
  const [pinnedItems, setPinnedItems] = useState<EduPortfolioItem[]>([])
  const [timelineItems, setTimelineItems] = useState<EduPortfolioItem[]>([])
  const [hiddenItems, setHiddenItems] = useState<EduPortfolioItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loadingPortfolio, setLoadingPortfolio] = useState<boolean>(false)
  const [loadingMore, setLoadingMore] = useState<boolean>(false)
  const [portfolioError, setPortfolioError] = useState<string | null>(null)
  const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false)
  const [loadingCertId, setLoadingCertId] = useState<string | null>(null)
  const [showHidden, setShowHidden] = useState<boolean>(false)
  const [hidingItemId, setHidingItemId] = useState<string | null>(null)

  // Portfolio fetch
  const fetchPortfolio = useCallback(
    async (cursor?: string) => {
      if (cursor) {
        setLoadingMore(true)
      } else {
        setLoadingPortfolio(true)
      }
      setPortfolioError(null)

      try {
        const res = await getEduPortfolio({
          workspaceId,
          learnerId,
          cursor,
          pageSize: 20,
        })
        if (cursor) {
          setTimelineItems((prev) => [...prev, ...(res.items || [])])
        } else {
          setPinnedItems(res.pinnedItems || [])
          setTimelineItems(res.items || [])
          if (Array.isArray(res.hiddenItems)) {
            setHiddenItems(res.hiddenItems)
          }
        }
        setNextCursor(res.nextCursor || null)
      } catch (err) {
        setPortfolioError(mapEduServerError(err, t))
      } finally {
        setLoadingPortfolio(false)
        setLoadingMore(false)
      }
    },
    [workspaceId, learnerId, t],
  )

  useEffect(() => {
    if (frozen || !workspaceId || !learnerId) return
    let active = true
    void Promise.resolve().then(() => {
      if (active) {
        setLoadingPtmSummaries(true)
        setPtmSummariesError(null)
      }
    })

    listFamilyEduPtmSummaries({ workspaceId, learnerId })
      .then((res) => {
        if (!active) return
        setPtmSummaries(res?.summaries || [])
      })
      .catch((err) => {
        if (!active) return
        setPtmSummariesError(mapEduServerError(err, t))
      })
      .finally(() => {
        if (active) setLoadingPtmSummaries(false)
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, reloadSeq, t])

  const sortedPtmSummaries = useMemo(() => {
    return [...ptmSummaries].sort(
      (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
    )
  }, [ptmSummaries])

  useEffect(() => {
    if (frozen) return
    let active = true
    void Promise.resolve().then(() => {
      if (!active) return
      void fetchPortfolio()
    })
    return () => {
      active = false
    }
  }, [frozen, fetchPortfolio])

  const handleHideItem = async (item: EduPortfolioItem) => {
    setHidingItemId(item.id)
    try {
      await setEduPortfolioFamilyHide({
        workspaceId,
        learnerId,
        itemId: item.id,
        hidden: true,
      })
      setPinnedItems((prev) => prev.filter((i) => i.id !== item.id))
      setTimelineItems((prev) => prev.filter((i) => i.id !== item.id))
      setHiddenItems((prev) => [item, ...prev.filter((i) => i.id !== item.id)])
    } catch (err) {
      setPortfolioError(mapEduServerError(err, t))
    } finally {
      setHidingItemId(null)
    }
  }

  const handleUnhideItem = async (item: EduPortfolioItem) => {
    setHidingItemId(item.id)
    try {
      await setEduPortfolioFamilyHide({
        workspaceId,
        learnerId,
        itemId: item.id,
        hidden: false,
      })
      setHiddenItems((prev) => prev.filter((i) => i.id !== item.id))
      if (item.pinned) {
        setPinnedItems((prev) => [item, ...prev])
      } else {
        setTimelineItems((prev) => [item, ...prev])
      }
    } catch (err) {
      setPortfolioError(mapEduServerError(err, t))
    } finally {
      setHidingItemId(null)
    }
  }

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true)
    setPortfolioError(null)
    try {
      const res = await getEduPortfolioPdfUrl({
        workspaceId,
        learnerId,
      })
      if (res.url) {
        window.open(res.url, '_blank')
      }
    } catch (err) {
      setPortfolioError(mapEduServerError(err, t))
    } finally {
      setDownloadingPdf(false)
    }
  }

  const handleViewCertificate = async (item: EduPortfolioItem) => {
    setLoadingCertId(item.id)
    try {
      const res = await getEduCertificatePdfUrl({
        workspaceId,
        certificateId: item.certificateId || item.sourceId,
      })
      if (res.url) {
        window.open(res.url, '_blank')
      }
    } catch (err) {
      setPortfolioError(mapEduServerError(err, t))
    } finally {
      setLoadingCertId(null)
    }
  }

  const formatItemDate = (dateVal: unknown): string => {
    return formatEduDate(dateVal)
  }

  const getKindLabel = (kind: EduPortfolioItem['kind']) => {
    switch (kind) {
      case 'HOMEWORK':
        return t('NLSEduPortfolioKindHomework')
      case 'FEED':
        return t('NLSEduPortfolioKindFeed')
      case 'CERTIFICATE':
        return t('NLSEduPortfolioKindCertificate')
      case 'PRACTICE':
        return t('NLSEduPortfolioKindPractice')
      default:
        return kind
    }
  }

  const hasTraining = Boolean(trainingProgrammes && trainingProgrammes.length > 0)

  useEffect(() => {
    if (frozen) return
    let active = true

    void (async () => {
      await Promise.resolve()
      if (!active) return
      setLoading(true)
      setError(null)

      try {
        const res = await listFamilyReportCards({ workspaceId, learnerId })
        if (!active) return
        const cardList = res.cards || []
        setCards(cardList)
        setActiveCardId((prev) => {
          if (cardList.length > 0 && (!prev || !cardList.some((c) => (c.cardId || c.id) === prev))) {
            return cardList[0].cardId || cardList[0].id
          }
          return prev
        })
      } catch (err: unknown) {
        if (!active) return
        const message = err instanceof Error ? err.message : String(err)
        setError(t('NLSFamilyProgressLoadError') || message)
      } finally {
        if (active) setLoading(false)
      }
    })()

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, reloadSeq, t])

  const sortedCards = useMemo(() => {
    return [...cards].sort((a, b) => {
      const timeA = a.versions?.[0]?.publishedAtMs || 0
      const timeB = b.versions?.[0]?.publishedAtMs || 0
      return timeB - timeA
    })
  }, [cards])

  useEffect(() => {
    if (sortedCards.length > 0 && (!activeCardId || !sortedCards.some((c) => (c.cardId || c.id) === activeCardId))) {
      void Promise.resolve().then(() => {
        setActiveCardId(sortedCards[0].cardId || sortedCards[0].id)
      })
    }
  }, [sortedCards, activeCardId])

  const activeSummary = useMemo(() => {
    return sortedCards.find((c) => (c.cardId || c.id) === activeCardId) || sortedCards[0] || null
  }, [sortedCards, activeCardId])

  useEffect(() => {
    void Promise.resolve().then(() => {
      setSelectedVersion(null)
    })
  }, [activeCardId, learnerId])

  useEffect(() => {
    if (frozen || !activeSummary) {
      void Promise.resolve().then(() => {
        setVersionCard(null)
        setCardError(null)
      })
      return
    }

    let active = true
    const cardId = activeSummary.cardId || activeSummary.id

    void (async () => {
      await Promise.resolve()
      if (!active) return
      setCardLoading(true)
      setCardError(null)

      try {
        const res = await getFamilyReportCard({
          workspaceId,
          cardId,
          ...(selectedVersion !== null ? { version: selectedVersion } : {}),
        })
        if (!active) return
        setVersionCard(res.card || null)
      } catch (err: unknown) {
        if (!active) return
        setVersionCard(null)
        const message = err instanceof Error ? err.message : String(err)
        setCardError(t('NLSFamilyProgressCardLoadError') || message)
      } finally {
        if (active) setCardLoading(false)
      }
    })()

    return () => {
      active = false
    }
  }, [workspaceId, activeSummary, selectedVersion, frozen, reloadCardSeq, t])

  if (frozen) {
    return (
      <div className="familyProgressTab" data-testid="family-progress-tab">
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyProgressTab__frozen">{t('NLSFamilyFrozenNotice')}</div>
        </FamilyCard>
        {hasTraining && (
          <FamilyTrainingSection
            workspaceId={workspaceId}
            learnerId={learnerId}
            trainingProgrammes={trainingProgrammes}
            frozen={true}
          />
        )}
      </div>
    )
  }

  const renderReportCardsSection = () => {
    if (loading && cards.length === 0) {
      return (
        <div className="familyProgressTab__loading">{t('NLSFamilyLoading')}</div>
      )
    }

    if (error) {
      return (
        <FamilyCard title={t('NLSFamilyErrorTitle')}>
          <div className="familyProgressTab__error">{error}</div>
          <div className="familyProgressTab__retry">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setReloadSeq((s) => s + 1)}
            >
              {t('NLSFamilyProgressRetry')}
            </Button>
          </div>
        </FamilyCard>
      )
    }

    if (cards.length === 0) {
      return (
        <div className="familyProgressTab__empty">{t('NLSFamilyProgressNoCards')}</div>
      )
    }

    if (cardError) {
      return (
        <FamilyCard title={t('NLSFamilyErrorTitle')}>
          <div className="familyProgressTab__error">{cardError}</div>
          <div className="familyProgressTab__retry">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setReloadCardSeq((s) => s + 1)}
            >
              {t('NLSFamilyProgressRetry')}
            </Button>
          </div>
        </FamilyCard>
      )
    }

    if (cardLoading && !versionCard) {
      return (
        <div className="familyProgressTab__loading">{t('NLSFamilyLoading')}</div>
      )
    }

    const currentDisplayCard = versionCard
    const isUpdated = Boolean(currentDisplayCard?.updated || (currentDisplayCard?.version && currentDisplayCard.version > 1))
    const versionsList = activeSummary?.versions || []
    const earlierVersions = versionsList.filter((v) => v.version !== activeSummary?.version)
    const signedPdfUrl = currentDisplayCard?.signedPdfUrl

    return (
      <div className="familyProgressTab__reportsContent">
        {hasTraining && (
          <h3 className="familyProgressTab__reportsHeading" style={{ margin: 'var(--space-4, 16px) 0 var(--space-3, 12px)', fontSize: 'var(--font-size-xl, 1.25rem)', color: 'var(--text-strong)' }}>
            {t('NLSFamilyReportCardsHeading')}
          </h3>
        )}
        <div className="familyProgressTab__header">
          <div className="familyProgressTab__versionControls">
            {sortedCards.length >= 1 && (
              <div className="familyProgressTab__termSelectWrapper">
                <Select<string>
                  ariaLabel={t('NLSFamilyProgressSelectTerm')}
                  value={activeCardId}
                  onChange={(val) => {
                    setActiveCardId(val)
                    setSelectedVersion(null)
                  }}
                  options={sortedCards.map((c) => ({
                    value: c.cardId || c.id,
                    label: c.snapshot?.term?.name || c.termName || c.termId || c.cardId || c.id,
                  }))}
                />
              </div>
            )}

            {earlierVersions.length > 0 && (
              <div className="familyProgressTab__versionSelectWrapper">
                <label htmlFor="earlier-versions-select">
                  {t('NLSFamilyProgressEarlierVersions')}
                </label>
                <select
                  id="earlier-versions-select"
                  aria-label={t('NLSFamilyProgressEarlierVersions')}
                  className="familyProgressTab__versionSelect"
                  value={selectedVersion ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    setSelectedVersion(val ? Number(val) : null)
                  }}
                >
                  <option value="">
                    {t('NLSFamilyProgressVersionLatest', { version: activeSummary?.version })}
                  </option>
                  {earlierVersions.map((v) => (
                    <option key={v.version} value={v.version}>
                      {t('NLSFamilyProgressVersionOption', { version: v.version })}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedVersion !== null && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSelectedVersion(null)}
              >
                {t('NLSFamilyProgressBackToLatest')}
              </Button>
            )}
          </div>

          <div className="familyProgressTab__actions">
            {isUpdated && (
              <StatusMark
                tone="Low"
                label={t('NLSFamilyProgressUpdatedBadge')}
              />
            )}

            {signedPdfUrl && (
              <a
                href={signedPdfUrl}
                download
                className="familyProgressTab__pdfLink"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('NLSFamilyProgressDownloadPdf')}
              </a>
            )}
          </div>
        </div>

        {currentDisplayCard?.snapshot && (
          <div className="familyProgressTab__cardWrapper">
            <ReportCardDocument
              snapshot={currentDisplayCard.snapshot}
              kind={currentDisplayCard.kind}
              signatureUrl={currentDisplayCard.snapshot.signer?.signatureUrl}
            />
          </div>
        )}
      </div>
    )
  }


  const renderPortfolioCard = (item: EduPortfolioItem, isHidden = false) => {
    const isCert = item.kind === 'CERTIFICATE'
    const dateStr = formatItemDate(item.date)

    return (
      <article
        key={item.id}
        className="familyProgressTab__portfolioCard"
        data-testid={`portfolio-item-${item.id}`}
      >
        <div className="familyProgressTab__portfolioCardHead">
          <div>
            <div className="familyProgressTab__portfolioBadges">
              <span className="familyProgressTab__kindBadge">{getKindLabel(item.kind)}</span>
              {item.pinned && (
                <span className="familyProgressTab__pinnedBadge" data-testid="portfolio-pinned-badge">
                  {t('NLSEduPortfolioPinnedBadge')}
                </span>
              )}
              {isHidden && (
                <span className="familyProgressTab__hiddenBadge" data-testid="portfolio-hidden-badge">
                  {t('NLSFamilyPortfolioHiddenBadge')}
                </span>
              )}
            </div>
            <h4 className="familyProgressTab__portfolioTitle">{item.title}</h4>
            {dateStr && (
              <div
                className="familyProgressTab__portfolioDate"
                data-testid={`portfolio-item-date-${item.id}`}
              >
                {dateStr}
              </div>
            )}
          </div>

          <div className="familyProgressTab__portfolioCardActions">
            {isCert && (
              <Button
                variant="tertiary"
                size="sm"
                disabled={loadingCertId === item.id}
                onClick={() => void handleViewCertificate(item)}
                data-testid={`view-cert-${item.id}`}
              >
                {t('NLSFamilyPortfolioViewCertificate')}
              </Button>
            )}
            {isHidden ? (
              <Button
                variant="tertiary"
                size="sm"
                disabled={hidingItemId === item.id}
                onClick={() => void handleUnhideItem(item)}
                data-testid={`unhide-btn-${item.id}`}
              >
                {t('NLSFamilyPortfolioUnhide')}
              </Button>
            ) : (
              <Button
                variant="tertiary"
                size="sm"
                disabled={hidingItemId === item.id}
                onClick={() => void handleHideItem(item)}
                data-testid={`hide-btn-${item.id}`}
              >
                {t('NLSFamilyPortfolioHideFromView')}
              </Button>
            )}
          </div>
        </div>

        {item.summary && (
          <p className="familyProgressTab__portfolioSummary">{item.summary}</p>
        )}

        {item.attachments && item.attachments.length > 0 && (
          <div className="familyProgressTab__attachmentsGrid">
            {item.attachments.map((att, idx) => {
              const isImg = att.contentType?.startsWith('image/')
              if (isImg && att.downloadUrl) {
                return (
                  <img
                    key={att.id || `${att.name}_${idx}`}
                    src={att.downloadUrl}
                    alt={att.name}
                    className="familyProgressTab__mediaThumb"
                  />
                )
              }
              return (
                <a
                  key={att.id || `${att.name}_${idx}`}
                  href={att.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="familyProgressTab__attachmentLink"
                  download
                >
                  {att.name}
                </a>
              )
            })}
          </div>
        )}
      </article>
    )
  }

  const renderPortfolioSection = () => {
    const hasItems = pinnedItems.length > 0 || timelineItems.length > 0 || (showHidden && hiddenItems.length > 0)

    return (
      <section className="familyProgressTab__portfolioSection" data-testid="family-portfolio-section">
        <div className="familyProgressTab__portfolioHeader">
          <h3 className="familyProgressTab__portfolioHeading">
            {t('NLSFamilyPortfolioHeading')}
          </h3>
          <div className="familyProgressTab__portfolioHeaderActions">
            <Toggle
              checked={showHidden}
              onChange={setShowHidden}
              label={t('NLSFamilyPortfolioShowHidden')}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleDownloadPdf()}
              disabled={downloadingPdf}
              data-testid="download-portfolio-pdf"
            >
              {downloadingPdf
                ? t('NLSFamilyPortfolioDownloadingPdf')
                : t('NLSFamilyPortfolioDownloadPdf')}
            </Button>
          </div>
        </div>

        {portfolioError && (
          <div className="familyProgressTab__error" role="alert" data-testid="portfolio-error">
            {portfolioError}
          </div>
        )}

        {loadingPortfolio && pinnedItems.length === 0 && timelineItems.length === 0 ? (
          <div className="familyProgressTab__loading">
            <MiniLoader />
          </div>
        ) : !hasItems ? (
          <FamilyCard>
            <div className="familyProgressTab__empty" data-testid="portfolio-empty">
              <div className="familyProgressTab__emptyTitle">
                {t('NLSFamilyPortfolioEmptyTitle')}
              </div>
              <div>{t('NLSFamilyPortfolioEmptyBody')}</div>
            </div>
          </FamilyCard>
        ) : (
          <div className="familyProgressTab__portfolioList">
            {pinnedItems.map((item) => renderPortfolioCard(item, false))}
            {timelineItems.map((item) => renderPortfolioCard(item, false))}
            {showHidden && hiddenItems.map((item) => renderPortfolioCard(item, true))}

            {nextCursor && (
              <div className="familyProgressTab__loadMore">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void fetchPortfolio(nextCursor)}
                  disabled={loadingMore}
                  data-testid="portfolio-load-more"
                >
                  {t('NLSFamilyPortfolioLoadMore')}
                </Button>
              </div>
            )}
          </div>
        )}
      </section>
    )
  }

  const renderPtmSummariesSection = () => {
    return (
      <section
        className="familyProgressTab__section"
        aria-label={t('NLSFamilyPtmSummariesSectionTitle')}
        data-testid="family-ptm-summaries-section"
      >
        <div className="familyProgressTab__sectionHeader">
          <h3 className="familyProgressTab__sectionTitle">
            {t('NLSFamilyPtmSummariesSectionTitle')}
          </h3>
        </div>

        {loadingPtmSummaries ? (
          <div className="familyProgressTab__loader" aria-label={t('NLSFamilyLoading')}>
            <MiniLoader />
          </div>
        ) : ptmSummariesError ? (
          <div className="familyProgressTab__error" role="alert">
            {ptmSummariesError}
          </div>
        ) : sortedPtmSummaries.length === 0 ? (
          <div className="familyProgressTab__emptyState" data-testid="ptm-summaries-empty">
            {t('NLSFamilyPtmEmptySummaries')}
          </div>
        ) : (
          <div className="familyProgressTab__cardList" data-testid="ptm-summaries-list">
            {sortedPtmSummaries.map((s) => (
              <div
                key={s.id}
                className="familyProgressTab__card"
                data-testid={`ptm-summary-card-${s.id}`}
              >
                <div
                  className="familyProgressTab__cardHeader"
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span className="familyProgressTab__cardTeacherName" style={{ fontWeight: 600 }}>
                    {s.teacherName}
                  </span>
                  <span className="familyProgressTab__cardDate" style={{ color: 'var(--muted)', fontSize: 13 }}>
                    {formatEduDate(s.publishedAt, locale)}
                  </span>
                </div>
                <div
                  className="familyProgressTab__cardBody"
                  style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}
                  data-testid="ptm-summary-content"
                >
                  {s.summary}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    )
  }

  const renderAttendanceCard = () => {
    if (progressError) {
      return (
        <FamilyCard title={t('NLSFamilyProgressAttendanceTitle')}>
          <div className="familyProgressTab__error">{progressError}</div>
          <div className="familyProgressTab__retry">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setReloadProgressSeq((s) => s + 1)}
            >
              {t('NLSFamilyProgressRetry')}
            </Button>
          </div>
        </FamilyCard>
      )
    }

    if (loadingProgress && !progressData) {
      return (
        <FamilyCard title={t('NLSFamilyProgressAttendanceTitle')}>
          <div className="familyProgressTab__loading">
            <MiniLoader />
          </div>
        </FamilyCard>
      )
    }

    const overall = progressData?.attendance?.overall
    const months = progressData?.attendance?.months ?? []
    const overallTotal = overall?.total ?? 0
    const overallPresent = overall?.present ?? 0
    const overallPercent = overallTotal > 0 ? Math.round((overallPresent / overallTotal) * 100) : 0

    return (
      <FamilyCard title={t('NLSFamilyProgressAttendanceTitle')}>
        <div className="familyProgressTab__overallPresent" data-testid="attendance-overall-percent">
          {t('NLSFamilyProgressOverallPresent', { percent: overallPercent })}
        </div>

        {months.length === 0 ? (
          <div className="familyProgressTab__empty">{t('NLSFamilyProgressNoAttendance')}</div>
        ) : (
          <div className="familyProgressTab__attendanceMonths">
            {months.map((m) => {
              const mTotal = m.total || (m.present + m.late + m.absent + m.excused) || 1
              const pPct = (m.present / mTotal) * 100
              const lPct = (m.late / mTotal) * 100
              const aPct = (m.absent / mTotal) * 100
              const ePct = (m.excused / mTotal) * 100
              const ariaLabel = t('NLSFamilyAttendanceBarAria', {
                month: m.month,
                present: m.present,
                late: m.late,
                absent: m.absent,
                excused: m.excused,
              })

              return (
                <div key={m.month} className="familyProgressTab__monthAttendance" data-testid={`attendance-month-${m.month}`}>
                  <div className="familyProgressTab__monthHeader">
                    <span className="familyProgressTab__monthLabel">{m.month}</span>
                  </div>
                  <div
                    className="familyProgressTab__stackedBar"
                    role="img"
                    aria-label={ariaLabel}
                  >
                    {m.present > 0 && (
                      <span
                        className="familyProgressTab__barSegment familyProgressTab__barSegment--present"
                        style={{ width: `${pPct}%` }}
                      />
                    )}
                    {m.late > 0 && (
                      <span
                        className="familyProgressTab__barSegment familyProgressTab__barSegment--late"
                        style={{ width: `${lPct}%` }}
                      />
                    )}
                    {m.absent > 0 && (
                      <span
                        className="familyProgressTab__barSegment familyProgressTab__barSegment--absent"
                        style={{ width: `${aPct}%` }}
                      />
                    )}
                    {m.excused > 0 && (
                      <span
                        className="familyProgressTab__barSegment familyProgressTab__barSegment--excused"
                        style={{ width: `${ePct}%` }}
                      />
                    )}
                  </div>
                  <div className="familyProgressTab__barLegend">
                    <span>{t('NLSFamilyAttendanceLegendPresent', { count: m.present })}</span>
                    <span>{t('NLSFamilyAttendanceLegendLate', { count: m.late })}</span>
                    <span>{t('NLSFamilyAttendanceLegendAbsent', { count: m.absent })}</span>
                    <span>{t('NLSFamilyAttendanceLegendExcused', { count: m.excused })}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </FamilyCard>
    )
  }

  const renderMarksCard = () => {
    if (progressError) {
      return (
        <FamilyCard title={t('NLSFamilyProgressMarksTitle')}>
          <div className="familyProgressTab__error">{progressError}</div>
          <div className="familyProgressTab__retry">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setReloadProgressSeq((s) => s + 1)}
            >
              {t('NLSFamilyProgressRetry')}
            </Button>
          </div>
        </FamilyCard>
      )
    }

    if (loadingProgress && !progressData) {
      return (
        <FamilyCard title={t('NLSFamilyProgressMarksTitle')}>
          <div className="familyProgressTab__loading">
            <MiniLoader />
          </div>
        </FamilyCard>
      )
    }

    const marks = progressData?.marks ?? []

    return (
      <FamilyCard title={t('NLSFamilyProgressMarksTitle')}>
        {marks.length === 0 ? (
          <div className="familyProgressTab__empty" data-testid="marks-empty-state">
            {t('NLSFamilyProgressNoMarks')}
          </div>
        ) : (
          <div className="familyProgressTab__marksList" data-testid="marks-list">
            {marks.map((entry) => (
              <div
                key={`${entry.subjectNodeId}_${entry.termId}`}
                className="familyProgressTab__markEntry"
                data-testid={`marks-subject-${entry.subjectNodeId}`}
              >
                <div className="familyProgressTab__subjectHeader">
                  <span className="familyProgressTab__subjectName">{entry.subjectName}</span>
                  <span className="familyProgressTab__termBadge">{entry.termName}</span>
                </div>
                <ul className="familyProgressTab__assessmentList">
                  {entry.assessments.map((ass, idx) => (
                    <li key={idx} className="familyProgressTab__assessmentItem" data-testid={`assessment-item-${idx}`}>
                      <span className="familyProgressTab__assessmentTitle">{ass.title}</span>
                      <span className="familyProgressTab__assessmentScore">
                        {ass.score != null ? ass.score : '—'} / {ass.maxMarks}
                        {ass.grade ? ` (${ass.grade})` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </FamilyCard>
    )
  }

  return (
    <div className="familyProgressTab" data-testid="family-progress-tab">
      {hasTraining && (
        <FamilyTrainingSection
          workspaceId={workspaceId}
          learnerId={learnerId}
          trainingProgrammes={trainingProgrammes}
          frozen={false}
        />
      )}
      {renderAttendanceCard()}
      {renderMarksCard()}
      {renderReportCardsSection()}
      {renderPortfolioSection()}
      {renderPtmSummariesSection()}
    </div>
  )
}

// Preserve existing component export
export default FamilyProgressTab
